import { AfterViewInit, Directive } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { StorageType } from 'mbs-ui-shared';
import { AgentType } from 'mbs-ui-shared@models/Computer';
import { PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { noop } from 'rxjs';
import { WizardHelpersSettings } from 'wizards/helpers/bases/base-for-steps-helper';
import { HyperVFormModel } from 'wizards/models/HyperV/hyper-v-form-model';
import { VmwareFormModel } from 'wizards/models/VMWare/vmware-form-model';
import { VirtualMachine, VirtualMachinesSelectedType } from 'wizards/models/select-virtual-machines-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WindowsFeatures } from 'wizards/services/wizard-steps.service';
import { WizardBaseClass } from './wizard-base.class';

const features = {
  [PlanTypes.BackupHyperV]: WindowsFeatures.hyperV,
  [PlanTypes.BackupVMware]: WindowsFeatures.vMware
};

@UntilDestroy()
@Directive()
export abstract class VMWizardBaseClass extends WizardBaseClass implements AfterViewInit {
  public readonly storageType = StorageType;

  public FormModel: HyperVFormModel | VmwareFormModel;

  public selectVMTitle = this.i18nPipe.transform('wizards:what_backup_step_title');
  public isAppAwareEnabled = RemoteManagementWizardsService.getWizardState(this.mainService).isAppAwareEnabled;
  public isClusterSupported = RemoteManagementWizardsService.getWizardState(this.mainService).isClusterSupported;
  public selectedMachines: Array<VirtualMachine> = [];
  public ignorableSteps = [this.stepsManager.titles.advancedSettings];

  protected isCBTAvailable = true;
  protected isClusterAbility = true;
  protected isHyperV = RemoteManagementWizardsService.getWizardState(this.mainService).isHyperV;

  public get getSelectedStorage(): StorageConnection {
    return this.FormModel.Storages?.CloudStorages?.find((s) => s.ID === this.FormModel?.whereBackupStep?.ConnectionID);
  }

  public get canShowAppAwareStep(): boolean {
    return this.isAppAwareEnabled && this.isCBTAvailable;
  }

  public get canShowClusterStep(): boolean {
    return this.isClusterSupported && this.isClusterAbility;
  }

  public get canShowSelectVirtualDiskStep(): boolean {
    const machinesFullyLoaded = !!this.FormModel?.selectVirtualMachinesStep?.machinesFullFormat?.length;

    return machinesFullyLoaded && this.machinesManuallySelected;
  }

  protected get machinesManuallySelected(): boolean {
    return this.FormModel.selectVirtualMachinesStep.type === VirtualMachinesSelectedType.Selected;
  }

  ngAfterViewInit() {
    super.ngAfterViewInit();

    if (this.mainService?.isOffline) return;

    if (![PlanTypes.BackupProxmox, PlanTypes.RestoreProxmox].includes(this.planType)) this.checkVMPlugin();

    if (this.isHyperV) {
      this.checkCBTAbility();
      if (!this.isRDMode) this.checkClusterAbility();
    }
  }

  defaultSettings(): WizardHelpersSettings {
    return {
      ...super.defaultSettings(),
      isClusterSupported: this.isClusterSupported,
      isClusterAbility: this.isClusterAbility,
      isAppAwareEnabled: this.isAppAwareEnabled,
      agentVersion: this.mainService.backupVersionUpdated
    };
  }

  protected updateStepData(plan, isDefault = false): void {
    this.setLoadedPlanAndRunChainRequestsForBackup(plan);

    if (isDefault) {
      // TODO: MBS-24778 Enable new schedule and retention in Backup Templates
      if (!this.isEdit && this.isNBF && !this.isUnix) {
        this.FormModel.scheduleStep.ScheduleType = 'noschedule';
        this.FormModel.scheduleStep.fullBackupData.enabled = false;
      }

      return this.setDefaultValue();
    }

    this.generateFormModel(plan);
    // TODO: MBS-24778 Enable new schedule and retention in Backup Templates
    const rightVersionForSchedule = this.mainService.backupVersionUpdated && +this.mainService.backupVersionUpdated.substring(0, 2) >= 80;
    if (!this.isEdit && this.isNBF && !this.isUnix && !rightVersionForSchedule) {
      this.FormModel.scheduleStep.ScheduleType = 'noschedule';
      this.FormModel.scheduleStep.fullBackupData.enabled = false;
    }
  }

  protected abstract generateFormModel(plan): void;

  protected abstract clearAppAwareData(newPlan): void;

  savePlan(newPlan) {
    if (!this.canShowAppAwareStep) {
      this.clearAppAwareData(newPlan);
    }

    super.savePlan(newPlan);
  }

  machinesSelectedHandler(machines: Array<VirtualMachine>): void {
    this.selectedMachines = [...machines];
  }

  private checkCBTAbility(): void {
    this.stepService
      .VMFeatureTest(this.mainService.hid, WindowsFeatures.hyperVChangedBlockTracking)
      .pipe(untilDestroyed(this))
      .subscribe((res) => void (this.isCBTAvailable = !!res?.data));
  }

  private checkClusterAbility(): void {
    this.stepService
      .VMFeatureTest(this.mainService.hid, WindowsFeatures.hyperVCluster)
      .pipe(untilDestroyed(this))
      .subscribe((res) => (this.isClusterAbility = !!res?.data));
  }

  /**
   * For virtual machines, we will ask the agent - does he have the plugin installed?
   * If not, try to install remotely.
   * @private
   */
  private checkVMPlugin(): void {
    this.stepService
      .VMFeatureTest(this.mainService.hid, features[this.planType])
      .pipe(untilDestroyed(this))
      .subscribe((res) => {
        if (res?.data === false) {
          this.installVMFeature();
        }
      });
  }

  /**
   * The method will call on the agent a task to install the appropriate plugin for Virtual Machines
   * @private
   */
  private installVMFeature(): void {
    this.stepService
      .getRemoteCommandData(
        {
          agentType: AgentType.Backup,
          commandType: 'InstallFeature',
          params: {
            Feature: features[this.planType]
          }
        },
        this.mainService.hid
      )
      .subscribe(noop);
  }
}
